import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import Flex from 'legacyfrontend/components/Flex';
import formatSecondsToHours from 'legacyfrontend/utils/formatSecondsToHours';
import { formatDate, formatTime } from 'legacyfrontend/utils/dateUtils';
import { useColors } from 'legacyfrontend/hooks/useColors';
import TooltipScale from '../FlightCard/TooltipScale';
import TooltipLuggage from '../FlightCard/TooltipLuggage';
import FlightSingleDetailDialog from './FlightSingleDetailDialog';

const FlightSingleInfoRow = ({ flight, kind }) => {
  const { coral } = useColors();
  const [scales, setScales] = useState([]);

  const flightData = useMemo(() => {
    const { segments } = flight;
    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];

    return {
      departureDate: firstSegment.departure_date_time,
      arrivalDate: lastSegment.arrival_date_time,
      departureCity: firstSegment.origin_city_name,
      arrivalCity: lastSegment.destination_city_name,
      hasScales: segments.length > 1,
      originId: firstSegment.origin_iata,
      destinationId: lastSegment.destination_iata,
      duration: flight.duration,
    };
  }, [flight]);

  const airlineLogos = useMemo(() => {
    return [...new Set(flight.segments.map((segment) => segment.logoCompany))].filter(Boolean);
  }, [flight]);

  useEffect(() => {
    const calculateScales = () => {
      const { segments } = flight;
      if (segments.length <= 1) {
        setScales([{ isNotScale: true }, { isNotScale: true }]);
        return;
      }

      const newScales = [{ isNotScale: true }];

      segments.slice(0, -1).forEach((currentSegment, index) => {
        const nextSegment = segments[index + 1];
        const waitTime = Math.max(
          (new Date(nextSegment.departure_date_time) - new Date(currentSegment.arrival_date_time)) /
            1000,
          0
        );

        newScales.push({
          id: `scale${index + 1}`,
          origin: `${currentSegment.destination_short_name}, ${currentSegment.destination_city_name}`,
          destination: `${nextSegment.destination_short_name}, ${nextSegment.destination_city_name}`,
          samePlane: false,
          technicalStop: false,
          sameAirport: currentSegment.destination_iata === nextSegment.origin_iata,
          waitTime,
          isNotScale: false,
        });
      });

      newScales.push({ isNotScale: true });
      setScales(newScales);
    };

    calculateScales();
  }, [flight]);

  return (
    <Flex className='flight_card__data'>
      <Stack
        className='flight_card_info_second_row'
        sx={{
          flexDirection: { xs: 'column', lg: 'row' },
          marginTop: { xs: '1rem' },
          pl: 2,
        }}
      >
        {/* Airline Logos */}
        {airlineLogos.length > 0 && (
          <Stack
            sx={{
              marginRight: { xs: 0, lg: 2 },
              marginBottom: { xs: 2, lg: 0 },
              justifyContent: 'center',
            }}
          >
            {airlineLogos.map((logoUrl, index) => (
              <img
                alt='Airline Logo'
                key={`logo-${index}`}
                src={logoUrl}
                style={{
                  maxWidth: '100px',
                  width: '100%',
                  height: 'auto',
                  marginTop: index > 0 ? '1rem' : 0,
                }}
              />
            ))}
          </Stack>
        )}

        <Stack
          className='flight_card_info'
          sx={{
            flexDirection: 'column',
            paddingLeft: { xs: 0, lg: '1rem' },
            paddingRight: { xs: 0, lg: '1rem' },
          }}
        >
          <Flex className='flight_card_data_jcsb'>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.departureDate)}
            </Typography>
            <Typography color={coral} fontSize='0.75rem'>
              {!flightData.hasScales
                ? 'Vuelo Directo'
                : `${scales.length - 2} ${scales.length > 3 ? 'escalas' : 'escala'}`}
            </Typography>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.arrivalDate)}
            </Typography>
          </Flex>

          <Flex
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div className='dashed-line' />
            <div
              style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              {scales.map((scale, index) => (
                <TooltipScale escala={scale} isNotScale={scale.isNotScale} key={`scale-${index}`} />
              ))}
            </div>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='bottom_label_flight'>{flightData.originId}</p>
            <p className='bottom_label_flight'>{flightData.destinationId}</p>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='location_text'>{flightData.departureCity}</p>
            <p className='location_text'>{flightData.arrivalCity}</p>
          </Flex>
        </Stack>
      </Stack>

      <Stack
        sx={{
          flexDirection: { xs: 'column-reverse', lg: 'row' },
          paddingTop: { xs: 2, lg: 'inherit' },
          justifyContent: { xs: 'flex-end', lg: 'center' },
        }}
      >
        <Stack flex={1} gap={1} justifyContent='center' sx={{ height: { xs: '94px', lg: 'auto' } }}>
          <TooltipLuggage checkedBag={flight.segments[0].baggage} />
          <Stack flexDirection='column' textAlign='center'>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>Duración:</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>
        </Stack>

        <Stack className='flight_card_info_four_row' sx={{ padding: { xs: 2 } }}>
          <FlightSingleDetailDialog
            destinationId={flightData.destinationId}
            duration={flightData.duration}
            flight={flight.segments}
            kind={kind}
            originId={flightData.originId}
            scales={scales}
          />
        </Stack>
      </Stack>
    </Flex>
  );
};

export default FlightSingleInfoRow;
